import { Replay } from '@mui/icons-material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Box, IconButton, Typography } from '@mui/material'
import React from 'react'

export interface DateCarouselItem {
    date: string
    day: string
    price: string
    isSelected: boolean
    isLowest: boolean
}

export interface DateCarouselProps {
    items: DateCarouselItem[]
    onPrev: () => void
    onNext: () => void
    onSelect: (index: number) => void
    canPrev: boolean
    canNext: boolean
    isRTL?: boolean
}

const DateCarousel: React.FC<DateCarouselProps> = ({
    items,
    onPrev,
    onNext,
    onSelect,
    canPrev,
    canNext,
    isRTL = false
}) => {
    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'center',
                py: 2,
                backgroundColor: 'background.paper',
                px: 2
            }}
        >
            <IconButton
                onClick={isRTL ? onNext : onPrev}
                disabled={isRTL ? !canNext : !canPrev}
                sx={{
                    color: 'textColors.subheading',
                    mr: 1,
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                }}
                size="small"
            >
                {isRTL ? (
                    <ArrowForwardIosIcon sx={{ fontSize: 16 }} fontSize="inherit" />
                ) : (
                    <ArrowBackIosNewIcon sx={{ fontSize: 16 }} fontSize="inherit" />
                )}
            </IconButton>

            <Box
                role="list"
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    maxWidth: 900,
                    listStyle: 'none',
                    p: 0,
                    m: 0,
                    gap: 0.5
                }}
            >
                {items.map((item, index) => (
                    <Box
                        key={`${item.date}-${index}`}
                        role="listitem"
                        onClick={() => onSelect(index)}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer',
                            px: 0.5,
                            userSelect: 'none',
                            flex: 1,
                            textAlign: 'center'
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: item.isSelected ? 'primary.main' : 'textColors.subheading',
                                fontWeight: item.isSelected ? 600 : 400,
                                fontSize: 12,
                                mb: 0.5,
                                whiteSpace: item.isSelected ? 'nowrap' : ''
                            }}
                        >
                            {item.day}, {item.date}
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: item.isSelected
                                    ? 'primary.main'
                                    : item.isLowest
                                      ? 'success.main'
                                      : 'textColors.heading',
                                fontWeight: item.isSelected ? 700 : 500,
                                fontSize: 12,
                                lineHeight: 1.1,
                                mb: 0.2
                            }}
                        >
                            {item.price == '-' ? (
                                <Replay sx={{ fontSize: 12, color: 'textColors.subheading' }} />
                            ) : (
                                item.price
                            )}
                        </Typography>
                        {item.isSelected && (
                            <Box
                                sx={{
                                    height: 3,
                                    bgcolor: 'primary.main',
                                    borderRadius: 2,
                                    mt: 0.5,
                                    width: '60%',
                                    mx: 'auto'
                                }}
                            />
                        )}
                    </Box>
                ))}
            </Box>

            <IconButton
                onClick={isRTL ? onPrev : onNext}
                disabled={isRTL ? !canPrev : !canNext}
                sx={{
                    color: 'textColors.subheading',
                    ml: 1,
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                }}
                size="small"
            >
                {isRTL ? (
                    <ArrowBackIosNewIcon sx={{ fontSize: 16 }} fontSize="inherit" />
                ) : (
                    <ArrowForwardIosIcon sx={{ fontSize: 16 }} fontSize="inherit" />
                )}
            </IconButton>
        </Box>
    )
}

export default DateCarousel
